var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { Icon } from '../Icon/index.js';
import { NavVerticalContext, useNavVerticalContext, } from './NavVerticalContext.js';
export const NavVertical = (_a) => {
    var { as, children, className, collapsed = false, size = 'md' } = _a, rest = __rest(_a, ["as", "children", "className", "collapsed", "size"]);
    const Component = as || 'nav';
    return (React.createElement(Component, Object.assign({ className: twMerge(clsx('flex flex-col space-y-8 isolate', collapsed ? 'items-center' : 'w-full', className)), "aria-label": "Sidebar" }, rest),
        React.createElement(NavVerticalContext.Provider, { value: { collapsed, size } }, children)));
};
const NavVerticalItem = props => {
    const { children, as, leadingIcon, trailingIcon, active = false, className } = props, rest = __rest(props, ["children", "as", "leadingIcon", "trailingIcon", "active", "className"]);
    const Component = as || 'a';
    const { collapsed, size } = useNavVerticalContext();
    const { backgroundType } = useBackgroundContext();
    const itemClsx = [
        backgroundType === 'main' && [
            active
                ? 'bg-background-pressed text-content-on-background-pressed'
                : 'bg-background text-content-on-background-subdued hover:bg-background-hovered hover:text-content-on-background-hovered',
        ],
        backgroundType === 'surface' && [
            active
                ? 'bg-surface-pressed text-content-pressed'
                : 'bg-surface text-content-subdued hover:bg-surface-hovered hover:text-content-hovered',
        ],
        backgroundType === 'secondary' && [
            active
                ? 'bg-action-primary-subdued text-content-primary'
                : 'bg-topbar text-content-on-topbar hover:bg-topbar hover:text-content-on-topbar',
        ],
        'focus:outline-none focus-visible:ring ring-inset ring-offset-0',
    ];
    const iconClsx = [
        backgroundType === 'main' && [
            active
                ? 'text-content-on-background-pressed'
                : 'text-content-on-background-subdued group-hover:text-content-on-background-hovered',
        ],
        backgroundType === 'surface' && [
            active
                ? 'text-content-pressed'
                : 'text-content-subdued group-hover:text-content-hovered',
        ],
        backgroundType === 'secondary' && [
            active
                ? 'text-content-primary'
                : 'text-content-on-topbar group-hover:text-content-on-topbar',
        ],
    ];
    const paddingClsx = [
        size === 'md' && ' px-3 py-2',
        size === 'lg' && ' px-4 py-4',
    ];
    const content = typeof children === 'string' ? (React.createElement("span", { className: "flex-grow truncate" }, children)) : (children);
    if (!leadingIcon && collapsed) {
        console.warn('leading icon is required when using collapsed');
    }
    return (React.createElement(Component, Object.assign({ className: clsx('group flex items-center leading-5 rounded-md w-full', itemClsx, paddingClsx, className), "aria-current": active ? 'page' : undefined }, rest),
        leadingIcon && (React.createElement(Icon, { className: clsx(iconClsx, 'flex-shrink-0 h-6 w-6', !collapsed && '-ms-1 me-3'), "aria-hidden": "true" }, leadingIcon)),
        !collapsed ? content : null,
        !collapsed && trailingIcon && (React.createElement(Icon, { className: clsx(iconClsx, 'flex-shrink-0 h-6 w-6 -me-1 ms-3'), "aria-hidden": "true" }, trailingIcon))));
};
const NavVerticalItemSecondary = props => {
    const { children, leadingIcon, active = false, className } = props, rest = __rest(props, ["children", "leadingIcon", "active", "className"]);
    const { collapsed } = useNavVerticalContext();
    const { backgroundType } = useBackgroundContext();
    const content = typeof children === 'string' ? (React.createElement("span", { className: "truncate" }, children)) : (children);
    if (!leadingIcon && collapsed) {
        console.warn('leading icon is required when using collapsed');
    }
    const itemClsx = [
        backgroundType === 'main' &&
            'text-content-on-background-subdued hover:text-content-on-background-hovered hover:bg-background-hovered',
        backgroundType === 'surface' &&
            'text-content-subdued hover:text-content-hovered hover:bg-surface-hovered',
        backgroundType === 'secondary' &&
            'text-content-on-topbar hover:text-content-on-topbar hover:bg-topbar',
    ];
    const iconClsx = [
        backgroundType === 'main' && [
            active
                ? 'text-content-on-background-subdued'
                : 'text-content-on-background-subdued group-hover:text-content-on-background-subdued',
        ],
        backgroundType === 'surface' && [
            active
                ? 'text-content-subdued'
                : 'text-content-subdued group-hover:text-content-hovered',
        ],
        backgroundType === 'secondary' && [
            active
                ? 'text-content-on-topbar'
                : 'text-content-on-topbar group-hover:text-content-on-topbar',
        ],
    ];
    return (React.createElement("a", Object.assign({ className: clsx('group flex items-center px-3 py-2 leading-none rounded-md', itemClsx, 'focus:outline-none focus-visible:ring', className), "aria-current": active ? 'page' : undefined }, rest),
        leadingIcon && (React.createElement(Icon, { className: clsx(iconClsx, 'flex-shrink-0 h-6 w-6', !collapsed && '-ms-1 me-3'), "aria-hidden": "true" }, leadingIcon)),
        !collapsed ? content : null));
};
export const NavVerticalGroup = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { size } = useNavVerticalContext();
    return (React.createElement("div", Object.assign({ className: clsx(size === 'lg' ? 'space-y-2' : 'space-y-1', className), role: "group" }, rest), children));
};
export const NavVerticalGroupHeader = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    return (React.createElement("h3", Object.assign({ className: clsx('px-2 text-sm font-semibold text-content-on-background-subdued', className) }, rest), children));
};
NavVertical.Item = NavVerticalItem;
NavVertical.ItemSecondary = NavVerticalItemSecondary;
NavVertical.Group = NavVerticalGroup;
NavVertical.GroupHeader = NavVerticalGroupHeader;
//# sourceMappingURL=NavVertical.js.map